import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { createPaymentSession, getBorrowerReviewForTransaction } from '../services/api';

const ActionButtons = ({ userRole, status, onUpdateStatus, transactionId, setSnackbar }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [hasSubmittedReview, setHasSubmittedReview] = useState(false);

    useEffect(() => {
        // Check if a review has already been submitted when the transaction is completed
        const checkReviewStatus = async () => {
            if (status === 'completed' && userRole === 'lender') {
                try {
                    const response = await getBorrowerReviewForTransaction(transactionId);
                    setHasSubmittedReview(!!response.data);
                } catch (error) {
                    console.error('Error checking review status:', error);
                    // If we can't check the review status, we'll assume no review exists
                    setHasSubmittedReview(false);
                }
            }
        };

        checkReviewStatus();
        console.log("the transaction ID is: " + transactionId)
    }, [status, userRole, transactionId]);

    const handlePayNow = async () => {
        if (!stripe || !elements) {
            setSnackbar({ open: true, message: 'Stripe has not loaded yet.', severity: 'error' });
            return;
        }

        setLoading(true);

        try {
            const session = await createPaymentSession(transactionId);
            const result = await stripe.redirectToCheckout({
                sessionId: session.data.sessionId,
            });

            if (result.error) {
                setSnackbar({ open: true, message: result.error.message, severity: 'error' });
            }
        } catch (err) {
            console.error('Error creating payment session:', err);
            setSnackbar({ open: true, message: 'Error initiating payment', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const renderButtons = () => {
        if (userRole === 'lender') {
            switch (status) {
                case 'pending':
                    return (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => onUpdateStatus('accepted')}
                            >
                                Accept Request
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                onClick={() => onUpdateStatus('declined')}
                                sx={{ mt: 1 }}
                            >
                                Decline Request
                            </Button>
                        </Box>
                    );
                case 'accepted':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            Waiting for borrower to make payment.
                        </Typography>
                    );
                case 'paid':
                    return (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => onUpdateStatus('in_progress')}
                            >
                                Mark Listing as Picked Up
                            </Button>
                        </Box>
                    );
                case 'in_progress':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            Transaction is in progress.
                        </Typography>
                    );
                case 'completed':
                    return (
                        <Box mt={2}>
                            {!hasSubmittedReview ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    component={Link}
                                    to={`/lender/review/borrower/${transactionId}`}
                                >
                                    Review Borrower
                                </Button>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    Review submitted. Transaction completed.
                                </Typography>
                            )}
                        </Box>
                    );
                case 'declined':
                case 'canceled':
                case 'failed':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            This booking has been {status}.
                        </Typography>
                    );
                default:
                    return null;
            }
        } else if (userRole === 'borrower') {
            switch (status) {
                case 'pending':
                    return (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                fullWidth
                                onClick={() => onUpdateStatus('canceled')}
                            >
                                Cancel Request
                            </Button>
                        </Box>
                    );
                case 'accepted':
                    return (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handlePayNow}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay Now'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                onClick={() => onUpdateStatus('canceled')}
                                sx={{ mt: 1 }}
                            >
                                Cancel Booking
                            </Button>
                        </Box>
                    );
                case 'paid':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            Waiting for lender to start the transaction.
                        </Typography>
                    );
                case 'in_progress':
                    return (
                        <Box mt={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => onUpdateStatus('completed')}
                            >
                                Mark as Returned
                            </Button>
                        </Box>
                    );
                case 'completed':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            Transaction completed.
                        </Typography>
                    );
                case 'declined':
                case 'canceled':
                case 'failed':
                    return (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            This booking has been {status}.
                        </Typography>
                    );
                default:
                    return null;
            }
        }
    };

    ActionButtons.propTypes = {
        userRole: PropTypes.oneOf(['borrower', 'lender']).isRequired,
        status: PropTypes.string.isRequired,
        onUpdateStatus: PropTypes.func.isRequired,
        transactionId: PropTypes.string.isRequired,
        setSnackbar: PropTypes.func.isRequired,
    };

    return <>{renderButtons()}</>;
};

export default ActionButtons;